/* eslint-disable @typescript-eslint/no-unused-vars */
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { format } from 'date-fns';
import { ReportContext } from '../contexts';
import {
  CensusReportList,
  ComplianceObj,
  EmpRoundsReport,
  Filter,
  Patient,
  PatientRoundsReport,
  RawEmpFreq,
  RoundHistory
} from '../../config/interfaces';
import { date } from '../../utils/helpers';

interface IGetByFacilityPayload {
  facilityId: string;
}

interface ISearchPatientHistoryPayload extends IGetByFacilityPayload {
  patientSearch: string;
}

interface IGetPatientRoundHistoryPayload extends IGetByFacilityPayload {
  patientId: string;
}

interface IGetEmployeeRoundHistoryPayload {
  employeeId: string;
  roundingTypes?: string[];
}

interface IGetComplianceReportPayload extends IGetByFacilityPayload {
  startday: string;
  endday: string;
}

interface IGetPatientRoundsReportData {
  roundReportList: PatientRoundsReport[];
  totalRecordCount: number;
}

interface IGetPatientRoundsReportDownloadPayload {
  facilityId: string;
  unitId: string[];
  query: string;
  roundedBy: string | string[];
  sortBy: string;
  sortOrder: string;
  startday: string;
  endday: string;
  isDownload?: boolean;
}

interface IGetPatientRoundsReportPayload
  extends IGetPatientRoundsReportDownloadPayload {
  page: number;
}

interface IGetCensusReportData {
  censusReports: CensusReportList[];
  totalResults: number;
}
interface IGetCensusReportDownloadPayload {
  facilityId: string;
  unitId: string[];
  patient: string;
  user34s: string[];
  sortBy: string;
  sortOrder: string;
  startday: string;
  endday: string;
  status?: string;
  isDownload?: boolean;
}
interface IGetCensusReportPayload extends IGetCensusReportDownloadPayload {
  page: number;
  limit: number;
}

interface IGetEmployeeRoundsReportDownloadPayload {
  facilityId: string;
  query: string;
  roundedBy: string | string[];
  templateName: string;
  departments?: string;
  sortBy: string;
  sortOrder: string;
  startday: string;
  endday: string;
  isDownload?: boolean;
}
interface IGetEmployeeRoundsReportPayload
  extends IGetEmployeeRoundsReportDownloadPayload {
  page: number;
}

interface IGetEmployeeRoundsReportData {
  roundReportList: EmpRoundsReport[];
  totalRecordCount: number;
}

interface IGetEmployeeFreqReportDownloadPayload {
  facilityId: string;
  query: string;
  sortBy: string;
  sortOrder: string;
  startday: string;
  endday: string;
}
interface IGetEmployeeFreqReportPayload
  extends IGetEmployeeFreqReportDownloadPayload {
  page: number;
}

interface IGetEmployeeFreqReportData {
  freqReportList: RawEmpFreq[];
  totalRecordCount: number;
}

/**
 * Redux slice for managing report-related state using Redux Toolkit's `createSlice`.
 *
 * This slice handles actions related to searching, fetching, filtering, and managing various reports,
 * including patient history, round history, compliance, census, employee rounds, and frequency reports.
 * It also manages pagination, sorting, searching, and filter state for these reports.
 *
 * @property {object} initialState - The initial state for the report context.
 * @property {Function} searchPatientHistory - Initiates searching patient history.
 * @property {Function} searchPatientHistorySuccess - Handles successful patient history search.
 * @property {Function} searchPatientHistoryFailure - Handles failed patient history search.
 * @property {Function} clearPatientSearch - Clears patient search results.
 * @property {Function} getPatientRoundHistory - Initiates fetching patient round history.
 * @property {Function} getPatientRoundHistorySuccess - Handles successful fetch of patient round history.
 * @property {Function} getPatientRoundHistoryFailure - Handles failed fetch of patient round history.
 * @property {Function} clearPatientRoundHistory - Clears patient round history.
 * @property {Function} getEmployeeRoundHistory - Initiates fetching employee round history.
 * @property {Function} getEmployeeRoundHistorySuccess - Handles successful fetch of employee round history.
 * @property {Function} getEmployeeRoundHistoryFailure - Handles failed fetch of employee round history.
 * @property {Function} clearEmployeeRoundHistory - Clears employee round history.
 * @property {Function} getComplianceReport - Initiates fetching compliance report and sets loading state.
 * @property {Function} getComplianceReportSuccess - Handles successful fetch of compliance report.
 * @property {Function} getComplianceReportFailure - Handles failed fetch of compliance report.
 * @property {Function} setComplianceReportFilter - Sets the filter for compliance report.
 * @property {Function} setpatientSummaryReportFilter - Sets the filter for compliance report from summary page.
 * @property {Function} clearpatientSummaryReportFilter - Clears the filter for compliance report.
 * @property {Function} getPatientRoundsReport - Initiates fetching patient rounds report and sets loading state.
 * @property {Function} getPatientRoundsReportSuccess - Handles successful fetch of patient rounds report.
 * @property {Function} getPatientRoundsReportFailure - Handles failed fetch of patient rounds report.
 * @property {Function} clearPatientRoundsReport - Clears patient rounds report data.
 * @property {Function} setPatientRoundsReportPage - Sets the current page for patient rounds report pagination.
 * @property {Function} setPatientRoundsReportSearchQuery - Sets the search query for patient rounds report.
 * @property {Function} setPatientRoundsReportSort - Sets the sort key and direction for patient rounds report.
 * @property {Function} getPatientRoundsReportDownload - Initiates download of patient rounds report.
 * @property {Function} getCensusReport - Initiates fetching census report and sets loading state.
 * @property {Function} getCensusReportSuccess - Handles successful fetch of census report.
 * @property {Function} getCensusReportFailure - Handles failed fetch of census report.
 * @property {Function} getEmployeeRoundsReport - Initiates fetching employee rounds report and sets loading state.
 * @property {Function} getEmployeeRoundsReportSuccess - Handles successful fetch of employee rounds report.
 * @property {Function} getEmployeeRoundsReportFailure - Handles failed fetch of employee rounds report.
 * @property {Function} clearEmployeeRoundsReport - Clears employee rounds report data.
 * @property {Function} setEmployeeRoundsReportPage - Sets the current page for employee rounds report pagination.
 * @property {Function} setEmployeeRoundsReportSearchQuery - Sets the search query for employee rounds report.
 * @property {Function} setEmployeeRoundsReportSort - Sets the sort key and direction for employee rounds report.
 * @property {Function} getEmployeeRoundsReportDownload - Initiates download of employee rounds report.
 * @property {Function} getEmployeeFreqReport - Initiates fetching employee frequency report and sets loading state.
 * @property {Function} getEmployeeFreqReportSuccess - Handles successful fetch of employee frequency report.
 * @property {Function} getEmployeeFreqReportFailure - Handles failed fetch of employee frequency report.
 * @property {Function} clearEmployeeFreqReport - Clears employee frequency report data.
 * @property {Function} setEmployeeFreqReportPage - Sets the current page for employee frequency report pagination.
 * @property {Function} setEmployeeFreqReportSearchQuery - Sets the search query for employee frequency report.
 * @property {Function} setEmployeeFreqReportSort - Sets the sort key and direction for employee frequency report.
 * @property {Function} getEmployeeFreqReportDownload - Initiates download of employee frequency report.
 * @property {Function} setRoundsReportFilter - Sets the filter for rounds reports and resets pagination.
 * @property {Function} clearRoundsReportFilter - Clears the rounds report filter and resets pagination.
 *
 * @example
 * dispatch(searchPatientHistory({ facilityId: '12345', patientSearch: 'Smith' }));
 * dispatch(getPatientRoundsReport({ facilityId: '12345', unitId: ['A.3N'], page: 1, ... }));
 * dispatch(setEmployeeRoundsReportSort({ sortKey: 'created', sortDir: 'desc' }));
 * dispatch(clearRoundsReportFilter());
 *
 * @returns {object} The report slice reducer and actions.
 */
const reportSlice = createSlice({
  name: 'report',
  initialState: ReportContext,
  reducers: {
    searchPatientHistory: (
      state,
      action: PayloadAction<ISearchPatientHistoryPayload>
    ) => {
      return {
        ...state
      };
    },
    searchPatientHistorySuccess: (state, action: PayloadAction<Patient[]>) => {
      return {
        ...state,
        searchedPatients: action.payload
      };
    },
    searchPatientHistoryFailure: state => {
      return {
        ...state,
        searchedPatients: []
      };
    },
    clearPatientSearch: state => {
      return {
        ...state,
        searchedPatients: []
      };
    },
    getPatientRoundHistory: (
      state,
      action: PayloadAction<IGetPatientRoundHistoryPayload>
    ) => {
      return {
        ...state
      };
    },
    getPatientRoundHistorySuccess: (
      state,
      action: PayloadAction<RoundHistory[]>
    ) => {
      return {
        ...state,
        patientRoundHistory: action.payload
      };
    },
    getPatientRoundHistoryFailure: state => {
      return {
        ...state,
        patientRoundHistory: []
      };
    },
    clearPatientRoundHistory: state => {
      return {
        ...state,
        patientRoundHistory: []
      };
    },
    getEmployeeRoundHistory: (
      state,
      action: PayloadAction<IGetEmployeeRoundHistoryPayload>
    ) => {
      return {
        ...state
      };
    },
    getEmployeeRoundHistorySuccess: (
      state,
      action: PayloadAction<RoundHistory[]>
    ) => {
      return {
        ...state,
        employeeRoundHistory: action.payload
      };
    },
    getEmployeeRoundHistoryFailure: state => {
      return {
        ...state,
        employeeRoundHistory: []
      };
    },
    clearEmployeeRoundHistory: state => {
      return {
        ...state,
        employeeRoundHistory: []
      };
    },
    getComplianceReport: (
      state,
      action: PayloadAction<IGetComplianceReportPayload>
    ) => {
      return {
        ...state,
        loading: true
      };
    },
    getEmpComplianceReport: (
      state,
      action: PayloadAction<IGetComplianceReportPayload>
    ) => {
      return {
        ...state,
        loading: true
      };
    },
    getComplianceReportSuccess: (
      state,
      action: PayloadAction<ComplianceObj[]>
    ) => {
      return {
        ...state,
        complianceReport: action.payload,
        loading: false
      };
    },

    getComplianceReportFailure: state => {
      return {
        ...state,
        complianceReport: [],
        loading: false
      };
    },
    setComplianceReportFilter: (state, action: PayloadAction<Filter>) => {
      return {
        ...state,
        complianceReportFilter: action.payload
      };
    },
    clearComplianceReportFilter: state => {
      return {
        ...state,
        complianceReportFilter: {} as Filter
      };
    },
    setpatientSummaryReportFilter: (state, action: PayloadAction<Filter>) => {
      return {
        ...state,
        patientSummaryReportFilter: action.payload
      };
    },
    clearpatientSummaryReportFilter: state => {
      return {
        ...state,
        patientSummaryReportFilter: {} as Filter
      };
    },
    setEmployeeSummaryReportFilter: (state, action: PayloadAction<Filter>) => {
      return {
        ...state,
        employeeSummaryReportFilter: action.payload
      };
    },
    clearEmployeeSummaryReportFilter: state => {
      return {
        ...state,
        employeeSummaryReportFilter: {} as Filter
      };
    },
    getPatientRoundsReport: (
      state,
      action: PayloadAction<IGetPatientRoundsReportPayload>
    ) => {
      return {
        ...state,
        loading: true
      };
    },
    getPatientRoundsReportSuccess: (
      state,
      action: PayloadAction<IGetPatientRoundsReportData>
    ) => {
      return {
        ...state,
        patientRoundsReport: action.payload.roundReportList,
        totalTableCount: action.payload.totalRecordCount,
        loading: false
      };
    },
    getPatientRoundsReportFailure: state => {
      return {
        ...state,
        patientRoundsReport: [],
        totalTableCount: 0,
        loading: false
      };
    },
    clearPatientRoundsReport: state => {
      return {
        ...state,
        patientRoundsReport: [],
        totalTableCount: 0
      };
    },
    setPatientRoundsReportPage: (state, action: PayloadAction<number>) => {
      return {
        ...state,
        currentPatientRoundsReportPage: action.payload
      };
    },
    setPatientRoundsReportSearchQuery: (
      state,
      action: PayloadAction<string>
    ) => {
      return {
        ...state,
        reportSearchQuery: action.payload,
        currentPatientRoundsReportPage: 1
      };
    },
    setPatientRoundsReportSort: (
      state,
      action: PayloadAction<{ sortKey: string; sortDir: string }>
    ) => {
      return {
        ...state,
        ...{
          sortRoundsKey: action.payload.sortKey,
          sortRoundsDir: action.payload.sortDir
        }
      };
    },
    getPatientRoundsReportDownload: (
      state,
      action: PayloadAction<IGetPatientRoundsReportDownloadPayload>
    ) => {
      return {
        ...state
      };
    },
    getCensusReport: (
      state,
      action: PayloadAction<IGetCensusReportPayload>
    ) => {
      return {
        ...state,
        loading: true
      };
    },
    getCensusReportSuccess: (
      state,
      action: PayloadAction<IGetCensusReportData>
    ) => {
      return {
        ...state,
        censusReport: action.payload.censusReports,
        totalTableCount: action.payload.totalResults,
        loading: false
      };
    },
    getCensusReportFailure: state => {
      return {
        ...state,
        censusReport: [],
        totalTableCount: 0,
        loading: false
      };
    },
    clearCensusReport: state => {
      return {
        ...state,
        censusReport: [],
        totalTableCount: 0
      };
    },
    setCensusReportPage: (state, action: PayloadAction<number>) => {
      return {
        ...state,
        currentCensusReportPage: action.payload
      };
    },
    setCensusReportSearchQuery: (state, action: PayloadAction<string>) => {
      return {
        ...state,
        reportSearchQuery: action.payload,
        currentCensusReportPage: 1
      };
    },
    setCensusReportSort: (
      state,
      action: PayloadAction<{ sortKey: string; sortDir: string }>
    ) => {
      return {
        ...state,
        ...{
          sortCensusKey: action.payload.sortKey,
          sortRoundsDir: action.payload.sortDir
        }
      };
    },
    getCensusReportExport: (
      state,
      action: PayloadAction<IGetCensusReportDownloadPayload>
    ) => {
      return {
        ...state
      };
    },
    getCensusReportDownload: (
      state,
      action: PayloadAction<IGetCensusReportDownloadPayload>
    ) => {
      return {
        ...state,
        isCensusDownload: true
      };
    },
    getEmployeeRoundsReport: (
      state,
      action: PayloadAction<IGetEmployeeRoundsReportPayload>
    ) => {
      return {
        ...state,
        loading: true
      };
    },
    getEmployeeRoundsReportSuccess: (
      state,
      action: PayloadAction<IGetEmployeeRoundsReportData>
    ) => {
      return {
        ...state,
        employeeRoundsReport: action.payload.roundReportList,
        totalTableCount: action.payload.totalRecordCount,
        loading: false
      };
    },
    getEmployeeRoundsReportFailure: state => {
      return {
        ...state,
        employeeRoundsReport: [],
        totalTableCount: 0,
        loading: false
      };
    },
    clearEmployeeRoundsReport: state => {
      return {
        ...state,
        employeeRoundsReport: []
      };
    },
    setEmployeeRoundsReportPage: (state, action: PayloadAction<number>) => {
      return {
        ...state,
        currentEmployeeRoundsReportPage: action.payload
      };
    },
    setEmployeeRoundsReportSearchQuery: (
      state,
      action: PayloadAction<string>
    ) => {
      return {
        ...state,
        reportSearchQuery: action.payload,
        currentEmployeeRoundsReportPage: 1,
        currentEmployeeFreqReportPage: 1
      };
    },
    setEmployeeRoundsReportSort: (
      state,
      action: PayloadAction<{ sortKey: string; sortDir: string }>
    ) => {
      return {
        ...state,
        ...{
          sortRoundsKey: action.payload.sortKey,
          sortRoundsDir: action.payload.sortDir
        }
      };
    },
    getEmployeeRoundsReportDownload: (
      state,
      action: PayloadAction<IGetEmployeeRoundsReportDownloadPayload>
    ) => {
      return {
        ...state
      };
    },
    getEmployeeFreqReport: (
      state,
      action: PayloadAction<IGetEmployeeFreqReportPayload>
    ) => {
      return {
        ...state,
        loading: true
      };
    },
    getEmployeeFreqReportSuccess: (
      state,
      action: PayloadAction<IGetEmployeeFreqReportData>
    ) => {
      return {
        ...state,
        employeeFreqReport: action.payload.freqReportList,
        totalTableCount: action.payload.totalRecordCount,
        loading: false
      };
    },
    getEmployeeFreqReportFailure: state => {
      return {
        ...state,
        employeeFreqReport: [],
        loading: false
      };
    },
    clearEmployeeFreqReport: state => {
      return {
        ...state,
        employeeFreqReport: []
      };
    },
    setEmployeeFreqReportPage: (state, action: PayloadAction<number>) => {
      return {
        ...state,
        currentEmployeeFreqReportPage: action.payload
      };
    },
    setEmployeeFreqReportSearchQuery: (
      state,
      action: PayloadAction<string>
    ) => {
      return {
        ...state,
        reportSearchQuery: action.payload,
        currentEmployeeFreqReportPage: 1
      };
    },
    setEmployeeFreqReportSort: (
      state,
      action: PayloadAction<{ sortKey: string; sortDir: string }>
    ) => {
      return {
        ...state,
        ...{
          sortFreqKey: action.payload.sortKey,
          sortFreqDir: action.payload.sortDir
        }
      };
    },
    getEmployeeFreqReportDownload: (
      state,
      action: PayloadAction<IGetEmployeeFreqReportDownloadPayload>
    ) => {
      return {
        ...state
      };
    },
    setRoundsReportFilter: (state, action: PayloadAction<Filter>) => {
      return {
        ...state,
        roundsReportFilter: action.payload,
        currentPatientRoundsReportPage: 1,
        currentEmployeeRoundsReportPage: 1,
        currentCensusReportPage: 1
      };
    },
    clearRoundsReportFilter: state => {
      return {
        ...state,
        roundsReportFilter: {} as Filter,
        currentPatientRoundsReportPage: 1,
        currentEmployeeRoundsReportPage: 1,
        currentCensusReportPage: 1
      };
    },
    setCensusReportFilter: (state, action: PayloadAction<Filter>) => {
      return {
        ...state,
        censusReportFilter: action.payload,
        currentCensusReportPage: 1
      };
    },
    clearCensusReportFilter: state => {
      return {
        ...state,
        censusReportFilter: {} as Filter,
        currentCensusReportPage: 1
      };
    },
    setFrequencyReportFilter: (state, action: PayloadAction<Filter>) => {
      return {
        ...state,
        frequencyReportFilter: action.payload,
        currentEmployeeFreqReportPage: 1
      };
    },
    clearFrequencyReportFilter: state => {
      return {
        ...state,
        frequencyReportFilter: {} as Filter,
        currentEmployeeFreqReportPage: 1
      };
    }
  }
});

export const {
  searchPatientHistory,
  searchPatientHistorySuccess,
  searchPatientHistoryFailure,
  clearPatientSearch,
  getPatientRoundHistory,
  getPatientRoundHistorySuccess,
  getPatientRoundHistoryFailure,
  clearPatientRoundHistory,
  getEmployeeRoundHistory,
  getEmployeeRoundHistorySuccess,
  getEmployeeRoundHistoryFailure,
  clearEmployeeRoundHistory,
  getComplianceReport,
  getEmpComplianceReport,
  getComplianceReportSuccess,
  getComplianceReportFailure,
  setComplianceReportFilter,
  clearComplianceReportFilter,
  setpatientSummaryReportFilter,
  clearpatientSummaryReportFilter,
  setEmployeeSummaryReportFilter,
  clearEmployeeSummaryReportFilter,
  getPatientRoundsReport,
  getPatientRoundsReportSuccess,
  getPatientRoundsReportFailure,
  clearPatientRoundsReport,
  setPatientRoundsReportPage,
  setPatientRoundsReportSearchQuery,
  setPatientRoundsReportSort,
  getPatientRoundsReportDownload,
  getCensusReport,
  getCensusReportSuccess,
  getCensusReportFailure,
  clearCensusReport,
  setCensusReportPage,
  setCensusReportSearchQuery,
  setCensusReportSort,
  getCensusReportExport,
  getCensusReportDownload,
  getEmployeeRoundsReport,
  getEmployeeRoundsReportSuccess,
  getEmployeeRoundsReportFailure,
  clearEmployeeRoundsReport,
  setEmployeeRoundsReportPage,
  setEmployeeRoundsReportSearchQuery,
  setEmployeeRoundsReportSort,
  getEmployeeRoundsReportDownload,
  getEmployeeFreqReport,
  getEmployeeFreqReportSuccess,
  getEmployeeFreqReportFailure,
  clearEmployeeFreqReport,
  setEmployeeFreqReportPage,
  setEmployeeFreqReportSearchQuery,
  setEmployeeFreqReportSort,
  getEmployeeFreqReportDownload,
  setRoundsReportFilter,
  clearRoundsReportFilter,
  setCensusReportFilter,
  clearCensusReportFilter,
  setFrequencyReportFilter,
  clearFrequencyReportFilter
} = reportSlice.actions;

export default reportSlice.reducer;
